import { useGetMe } from 'api-hooks/auth/query';
import { GuestModel } from 'api-hooks/guest';
import {
  TicketSaleFlightSaleMutationInput,
  TicketSaleModel,
  TicketSaleMutationInput,
} from 'api-hooks/sale/ticket-sale';
import { AuthorizationRules } from 'common/constants';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import { CustomLabel } from 'components/elements/custom-form-control';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TitleStatus from 'legacy-modules/common/title-status';
import useCreateGuestAction from 'legacy-modules/master/guest/hooks/use-create-guest-action';
import SalePurchaseRefundTable from 'legacy-modules/refund/components/sale-purchase-table';
import ParentSaleRelatedTable from 'legacy-modules/sales/component/parent-children-sales-table';
import PurchaseRelatedTable from 'legacy-modules/sales/component/purchase-related-table';
import SalesActionComponent from 'legacy-modules/sales/component/sales-action-component';
import RescheduleHistoryTable from 'legacy-modules/sales/reschedule/reschedule-history-table';
import {
  Container,
  FormContent,
  FormDivider,
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import {
  TicketSaleFlightGuestFormType,
  TicketSaleFlightSaleFormType,
  TicketSaleFormSchema,
  TicketSaleFormType,
  TicketSaleMethodType,
} from './form-type';
import TicketSaleInvoiceSelectInput from './invoice-select-input';
import OpenTicketCard from './open-ticket/card';
import SalesDetailCard from './sales-detail-card';
import ScheduleSection from './schedule';
import SummaryFlightSale from './summary';
import useGetDefaultValues, {
  addNewGuestItem,
  guestNameTransformer,
  guestPreferencesTransformer,
} from '../hooks/use-get-default-values';
import { OtherContainer } from '../style';

interface Props {
  ticketSale?: TicketSaleModel;
  onSubmit: (
    input: TicketSaleMutationInput,
    form: TicketSaleMethodType,
  ) => Promise<TicketSaleModel | undefined>;
  renderError?: React.ReactNode;
  defaultValues?: TicketSaleFormType;
}

export default function TicketSaleForm(props: Props) {
  const { t } = useTranslation();
  const { ticketSale, renderError } = props;
  const { can } = useAuthorization();
  const { asPath } = useRouter();
  const defaultValues = useGetDefaultValues({
    ticketSale,
    defaultValues: props.defaultValues,
  });

  const resolver = useYupValidationResolver(TicketSaleFormSchema());

  const methods = useForm<TicketSaleFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: TicketSaleFormType) => {
      const {
        flightSales,
        tempTransactionAt,
        customerBillingOptions,
        customerStatus,
        hasDueReceivable,
        address,
        phone,
        remainingTransactionQuota,
        isExceedingQuota,
        //remove unused data
        ...rest
      } = values;
      try {
        const _flightSales: TicketSaleFlightSaleMutationInput[] =
          flightSales.map((fs, index) => {
            return {
              ...fs,
              flightSaleGuests: fs.flightSaleGuests.map((fsg, guestIndex) => {
                return {
                  ...fsg,
                  preferences: fsg.preferences.map((fsgp) => fsgp.id),
                };
              }),
            };
          });

        const needApproval =
          values.transactionAt!.getTime() !== tempTransactionAt!.getTime();

        const result = {
          ...rest,
          flightSales: _flightSales,
          needApproval,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const { data: employeeData } = useGetMe();
  const employee = employeeData?.data;

  const isContribute =
    ticketSale?.admin.id === employee?.id ||
    ticketSale?.salesman?.id === employee?.id ||
    ticketSale?.operator?.id === employee?.id;

  const core = can(AuthorizationRules.SalesCore);

  const onRenderComponent = ticketSale && (isContribute || core) && (
    <>
      <SalesActionComponent
        data={ticketSale}
        defaultValues={defaultValues}
        type="flight-sales"
      />
    </>
  );

  const { getValues, setValue } = methods;
  const onSuccessCreatGuest = React.useCallback(
    (guest: GuestModel) => {
      const guestName = guestNameTransformer(guest);
      const guestPreferences = guestPreferencesTransformer(guest);
      const flightSales: TicketSaleFlightSaleFormType[] = getValues(
        'flightSales',
      ).map((fs) => {
        const flightSaleGuests: TicketSaleFlightGuestFormType[] = [
          ...fs.flightSaleGuests,
          {
            ...addNewGuestItem(),
            guestName,
            guestId: guest.id,
            guestPreferences,
          },
        ];
        return {
          ...fs,
          flightSaleGuests,
        };
      });
      setValue('flightSales', flightSales);
    },
    [getValues, setValue],
  );
  const { createGuestComponent } = useCreateGuestAction({
    onSuccess: onSuccessCreatGuest,
  });

  const onRelatedInvoiceAfterChange = React.useCallback(
    (value: TicketSaleModel) => {
      setValue('parentRefId', value.refId);
      setValue('operatorId', value.operator?.id || '');
      setValue('salesmanId', value.salesman?.id || '');
      setValue('customerId', value.customer?.id || '');
      setValue('customerBillingId', value.customerBillingAddress?.id || '');
    },
    [setValue],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!ticketSale}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        ticketSale && `${t('common:ticket_sale')} - ${ticketSale.invoiceNumber}`
      }
      keyName={asPath}
    >
      <FormContent>
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={ticketSale}
              noSubmit
              noCancel
              noDelete
              noEdit
              title={
                ticketSale ? (
                  <TitleStatus
                    type="sale-default"
                    isCompleted={ticketSale.isCompleted}
                    status={ticketSale.status}
                    title={`${t('common:ticket_sale')} - ${
                      ticketSale.invoiceNumber
                    }`}
                  />
                ) : (
                  t('common:add_extra', { extra: t('common:ticket_sale') })
                )
              }
              onRenderComponent={
                editable ? createGuestComponent : onRenderComponent
              }
            />
          )}
        </FormState>
        {renderError}
        <div>
          {ticketSale && !!(ticketSale?.releaseSales || []).length && (
            <SalePurchaseRefundTable data={ticketSale.releaseSales} />
          )}
          {ticketSale &&
            !!ticketSale.purchases.length &&
            (isContribute || core) && (
              <PurchaseRelatedTable
                data={ticketSale.purchases}
                navigation="FlightPurchasesView"
              />
            )}
          {ticketSale && ticketSale.isOpenTicket && (
            <OpenTicketCard ticketSale={ticketSale} />
          )}
          {ticketSale && (
            <RescheduleHistoryTable saleId={ticketSale.id} type="flight" hide />
          )}
          {ticketSale &&
            (!!ticketSale.parentSale ||
              !!(ticketSale.childrenSales || []).length) && (
              <ParentSaleRelatedTable
                data={ticketSale}
                navigation="TicketSalesView"
              />
            )}
          <RawSectionContainer>
            <HalfContainer>
              <TicketSaleInvoiceSelectInput
                label={t('common:related_invoice')}
                placeholder={t('common:choose_invoice')}
                name="parentRefId"
                onAfterDetailChange={onRelatedInvoiceAfterChange}
                isSales
              />
            </HalfContainer>
          </RawSectionContainer>
          <RawSectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="code"
                label={t('ticketSales:group_code')}
                placeholder={t('ticketSales:group_code')}
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
          </RawSectionContainer>
          <SeparatorVertical $margin={16} />
          <SalesDetailCard />
          <SeparatorVertical $margin={20}>
            <div style={{ height: 1 }} />
          </SeparatorVertical>
          <ScheduleSection />
          <SeparatorVertical $margin={24}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>

          <Container $style={{ height: 'auto' }}>
            <OtherContainer>
              <CustomLabel label={t('common:discount')} />
              <Input
                type="number"
                name="discount"
                placeholder={t('common:discount')}
                textAlign="right"
                clearable={false}
                noMargin
              />
            </OtherContainer>
            <SeparatorVertical $margin={16} />
            <OtherContainer>
              <CustomLabel label={t('common:other')} />
              <Input
                type="number"
                name="otherCost"
                placeholder={t('common:other')}
                textAlign="right"
                clearable={false}
                noMargin
              />
            </OtherContainer>
          </Container>
          <SeparatorVertical $margin={32} />
          <FormDivider />
        </div>
      </FormContent>
      <SummaryFlightSale />
    </Form>
  );
}
